import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, Clock } from 'lucide-react'
import type { ChatMessage, ChatSystemEvent } from '@manaro/contracts'
import { cn } from '@/shared/lib/cn.ts'
import { formatTime } from '@/shared/lib/format.ts'
import { isNearBottom, layoutMessages, splitLinks } from '../chat-format.ts'

const systemText = (message: ChatMessage): string => {
  const who = message.authorName ?? 'Оператор'
  const labels: Record<ChatSystemEvent, string> = {
    escalated: 'Нужен оператор — команде ушло уведомление',
    operator_joined: `${who} в разговоре`,
    operator_left: `Разговор возвращён боту · ${who}`,
    closed: 'Разговор закрыт',
    bot_unavailable: 'Бот не смог ответить',
  }
  return labels[message.text as ChatSystemEvent] ?? message.text
}

const BUBBLE: Record<'visitor' | 'bot' | 'operator', string> = {
  visitor: 'bg-surface text-ink border border-line-soft',
  bot: 'bg-brand-tint text-ink',
  operator: 'bg-brand text-white',
}

function MessageText({ text, onDark }: { text: string; onDark: boolean }) {
  return (
    <>
      {splitLinks(text).map((part, index) =>
        part.kind === 'link' ? (
          <a
            key={index}
            href={part.value}
            target="_blank"
            rel="noreferrer"
            className={cn('break-all underline underline-offset-2', onDark ? 'text-white' : 'text-brand-ink')}
          >
            {part.value}
          </a>
        ) : (
          <span key={index}>{part.value}</span>
        ),
      )}
    </>
  )
}

interface MessageListProps {
  messages: ChatMessage[]
  botTyping: boolean
  /** Text being sent right now — shown at once, like messengers do, until the server confirms it. */
  pendingText: string | null
}

export function MessageList({ messages, botTyping, pendingText }: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const stickToBottom = useRef(true)
  const [unseen, setUnseen] = useState(0)
  const rows = useMemo(() => layoutMessages(messages), [messages])
  const lastId = messages.at(-1)?.id
  const seenCount = useRef(messages.length)

  const scrollToBottom = (smooth: boolean) => {
    const element = scrollRef.current
    if (!element) return
    element.scrollTo({ top: element.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
    setUnseen(0)
    seenCount.current = messages.length
  }

  // Opening a conversation lands on the latest message.
  useLayoutEffect(() => {
    scrollToBottom(false)
  }, [])

  // New messages follow the reader only if they were already at the end; otherwise count them.
  useEffect(() => {
    if (stickToBottom.current || pendingText) {
      scrollToBottom(false)
    } else {
      setUnseen(Math.max(0, messages.length - seenCount.current))
    }
  }, [lastId, botTyping, pendingText])

  // The keyboard opening or the composer growing shrinks the list: keep the last message in view.
  useEffect(() => {
    const element = scrollRef.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      if (stickToBottom.current) element.scrollTop = element.scrollHeight
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const lastVisitorText = useMemo(() => messages.findLast((message) => message.role === 'visitor')?.text ?? '', [messages])

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scrollRef}
        onScroll={(event) => {
          stickToBottom.current = isNearBottom(event.currentTarget)
          if (stickToBottom.current && unseen > 0) {
            setUnseen(0)
            seenCount.current = messages.length
          }
        }}
        className="h-full overflow-y-auto overscroll-contain px-3 py-4 md:px-5"
      >
        <div className="mx-auto flex max-w-[760px] flex-col">
          {rows.map(({ message, day, firstInRun, lastInRun }) => (
            <div key={message.id} className="flex flex-col">
              {day && (
                <div className="sticky top-0 z-10 my-3 self-center rounded-full bg-surface-hover px-3 py-1 text-[12px] font-medium text-ink-muted">
                  {day}
                </div>
              )}
              {message.role === 'system' ? (
                <p className="my-2 self-center px-6 text-center text-[12.5px] text-ink-muted">
                  {systemText(message)} · <span className="tabular">{formatTime(message.createdAt)}</span>
                </p>
              ) : (
                <div
                  className={cn(
                    'flex flex-col',
                    message.role === 'visitor' ? 'items-start' : 'items-end',
                    lastInRun ? 'mb-2.5' : 'mb-1',
                  )}
                >
                  {firstInRun && message.role !== 'visitor' && (
                    <span className="mb-1 px-3 text-[12px] font-medium text-ink-muted">
                      {message.role === 'bot' ? 'Бот' : (message.authorName ?? 'Оператор')}
                    </span>
                  )}
                  <div
                    className={cn(
                      'max-w-[86%] rounded-[18px] px-3.5 pt-2 pb-1.5 text-[15px] leading-[1.45] whitespace-pre-wrap [overflow-wrap:anywhere] md:max-w-[75%]',
                      BUBBLE[message.role],
                      lastInRun && (message.role === 'visitor' ? 'rounded-bl-[6px]' : 'rounded-br-[6px]'),
                    )}
                  >
                    <MessageText text={message.text} onDark={message.role === 'operator'} />
                    <span
                      className={cn(
                        'tabular float-right mt-1.5 ml-3 text-[11px] leading-none',
                        message.role === 'operator' ? 'text-white/70' : 'text-ink-faint',
                      )}
                    >
                      {formatTime(message.createdAt)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}

          {pendingText && (
            <div className="mb-2.5 flex flex-col items-end opacity-70">
              <div className="max-w-[86%] rounded-[18px] rounded-br-[6px] bg-brand px-3.5 pt-2 pb-1.5 text-[15px] leading-[1.45] whitespace-pre-wrap text-white [overflow-wrap:anywhere] md:max-w-[75%]">
                {pendingText}
                <Clock className="float-right mt-1.5 ml-3 size-3 text-white/70" aria-label="Отправляется" />
              </div>
            </div>
          )}

          {botTyping && (
            <div className="mb-2 flex items-center gap-1.5 self-end rounded-[18px] bg-brand-tint px-3.5 py-2.5 text-[13px] text-ink-muted">
              <span className="flex gap-1" aria-hidden>
                <span className="size-1.5 animate-bounce rounded-full bg-ink-faint [animation-delay:-0.3s]" />
                <span className="size-1.5 animate-bounce rounded-full bg-ink-faint [animation-delay:-0.15s]" />
                <span className="size-1.5 animate-bounce rounded-full bg-ink-faint" />
              </span>
              Бот печатает
            </div>
          )}
        </div>
      </div>

      {/* Screen readers hear what the visitor wrote last, not the whole list on every poll. */}
      <p className="sr-only" aria-live="polite">
        {lastVisitorText && `Посетитель: ${lastVisitorText}`}
      </p>

      {unseen > 0 && (
        <button
          type="button"
          onClick={() => scrollToBottom(true)}
          className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-ink px-3.5 py-2 text-[13px] font-medium text-white shadow-panel"
        >
          <ArrowDown className="size-4" aria-hidden />
          {unseen === 1 ? 'Новое сообщение' : `Новые сообщения: ${unseen}`}
        </button>
      )}
    </div>
  )
}
